import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { IWorktask } from '@shared/services/worktask.service';
import { UUID } from 'node:crypto';
import { MesShiftService } from '../mes-shift.service';

export interface IMergeShiftsInput {
	shiftIds: UUID[];
	employeeName: string;
	start: Date;
	end: Date | null;
	worktasks: IWorktask[];
}

@Component({
	selector: 'app-merge-shifts',
	templateUrl: './merge-shifts.component.html',
	styleUrl: './merge-shifts.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MergeShiftsComponent {
	@Output() merged = new EventEmitter<UUID | null>();

	private readonly apiErrorPatterns: { pattern: RegExp; key: string }[] = [
		{
			pattern: /At least two different shifts are required to merge/i,
			key: 'too-few-shifts',
		},
		{
			pattern: /belong to the same employee/i,
			key: 'different-employees',
		},
		{
			pattern: /is still open and only the last shift being merged may be open/i,
			key: 'shift-still-open',
		},
		{
			pattern: /more than one timestamp exists at (?<timestamp>\S+)/i,
			key: 'timestamps-at-same-time',
		},
		{ pattern: /Shift cannot overlap existing shift/i, key: 'shift-overlap' },
		{
			pattern: /overlaps shift with id .* and they cannot be merged/i,
			key: 'sources-overlap',
		},
		{ pattern: /is approved and cannot be modified/i, key: 'shift-approved' },
		{ pattern: /Shift with id .* does not exist/i, key: 'shift-not-found' },
		{
			pattern: /gap between the shifts cannot be the off work task/i,
			key: 'gap-worktask-is-offtask',
		},
		{
			pattern: /duration of a shift cannot exceed (?<hours>[\d.,]+) hours/i,
			key: 'shift-exceeds-max-duration',
		},
	];
	gapWorkTaskId: UUID | null = null;
	loading = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IMergeShiftsInput,
		private dialogRef: MatDialogRef<MergeShiftsComponent>,
		private shiftservice: MesShiftService,
		private notificationService: NotificationService,
	) {}

	onGapWorkTaskChange(value: IWorktask | string | null): void {
		this.gapWorkTaskId = typeof value === 'string' ? (value as UUID) : null;
	}

	cancel(): void {
		this.dialogRef.close();
	}

	private translateApiError(apiMessage: string): [string, object] {
		const match = this.apiErrorPatterns.find((p) => p.pattern.test(apiMessage));
		if (match) {
			return [`mes.administration.shift-approval.merge.api-errors.${match.key}`, match.pattern.exec(apiMessage)?.groups ?? {}];
		}
		return ['mes.administration.shift-approval.merge.notifications.error.content', { message: apiMessage }];
	}

	merge(): void {
		if (this.loading) return;

		this.loading = true;
		this.shiftservice.mergeShifts(this.data.shiftIds, this.gapWorkTaskId).subscribe({
			next: () => {
				this.loading = false;
				this.merged.emit(this.gapWorkTaskId);
				this.dialogRef.close();
			},
			error: (error) => {
				this.loading = false;
				this.notificationService.notify(['mes.administration.shift-approval.merge.notifications.error.header'], this.translateApiError(error?.error?.error?.message ?? error?.message ?? ''), 'error');
			},
		});
	}
}
